import React from 'react';
import { usePermissions } from '../contexts/PermissionContext';

interface PermissionGateProps {
  /** Permission code required to render children, e.g. "BILLING_PAY" */
  permission?: string;
  /** Module code that must be enabled, e.g. "AI_ASSISTANT" */
  module?: string;
  /** Fallback rendered when access is denied. Default: nothing. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Renders children only when the authenticated user holds the specified
 * permission and/or the module is enabled for their clinic.
 *
 * Usage:
 *   <PermissionGate permission="BILLING_PAY">
 *     <PayButton />
 *   </PermissionGate>
 *
 *   <PermissionGate module="AI_ASSISTANT" fallback={<p>AI not available</p>}>
 *     <AiChat />
 *   </PermissionGate>
 */
export function PermissionGate({ permission, module: moduleCode, fallback = null, children }: PermissionGateProps) {
  const { can, moduleEnabled, isLoading, isSuperAdmin } = usePermissions();

  // While loading, render nothing (avoid flash of hidden content)
  if (isLoading) return null;

  // super_admin bypasses all gates
  if (isSuperAdmin) return <>{children}</>;

  if (permission && !can(permission)) return <>{fallback}</>;
  if (moduleCode && !moduleEnabled(moduleCode)) return <>{fallback}</>;

  return <>{children}</>;
}
